/**
 * Closing-team coverage API — wraps the `/closing/*` endpoints
 * (frontend_documentation/closing-coverage-calendar.md):
 *   1. Out-of-office coverage (OOO) entries + the team calendar.
 *   2. Bank holidays / wire-block list.
 *   3. User timezone on the profile.
 *
 * All calls go through `losHttp` (the eastview-os backend, same as the pipeline).
 * Datetimes round-trip as `yyyy-MM-ddTHH:mm:ssZ`; query params use `yyyy-MM-dd`.
 */
import { losHttp } from "../lib/losHttp";

export const closingCoverageApi = {
  // ── Out-of-office (coverage) ───────────────────────────────────────────────
  // GET /closing/calendar?start&end&group → { entries: [...] } overlapping the window.
  getTeamCalendar: ({ start, end, group } = {}) =>
    losHttp
      .get("/closing/calendar", {
        params: { start, end, ...(group ? { group } : {}) },
        logName: "Closing Coverage Calendar",
      })
      .then((r) => r.data),

  // GET /closing/me/ooo → { entries: [...] } (the caller's own active entries).
  getMyOoo: () =>
    losHttp.get("/closing/me/ooo", { logName: "My OOO" }).then((r) => r.data),

  // POST /closing/ooo { start, end, allDay, reason, backupEmail?, note? } → 201 entry.
  createOoo: (body) =>
    losHttp.post("/closing/ooo", body, { logName: "Create OOO" }).then((r) => r.data),

  // PUT /closing/ooo/{id} (partial). Own entries only (admins: any) else 403.
  updateOoo: (id, body) =>
    losHttp.put(`/closing/ooo/${id}`, body, { logName: "Update OOO" }).then((r) => r.data),

  // DELETE /closing/ooo/{id} → { deleted: 1 }.
  deleteOoo: (id) =>
    losHttp.delete(`/closing/ooo/${id}`, { logName: "Delete OOO" }).then((r) => r.data),

  // ── Bank holidays / wire-block ─────────────────────────────────────────────
  // GET /closing/bank-holidays?start&end → { holidays: [...] }. Read is open to all.
  getBankHolidays: ({ start, end } = {}) =>
    losHttp
      .get("/closing/bank-holidays", {
        params: { start, end },
        logName: "Bank Holidays",
      })
      .then((r) => r.data),

  // POST /closing/bank-holidays { holidayDate, name, wireBlocked, note? } → upsert by date.
  upsertBankHoliday: (body) =>
    losHttp
      .post("/closing/bank-holidays", body, { logName: "Upsert Bank Holiday" })
      .then((r) => r.data),

  // PUT /closing/bank-holidays/{id} (partial: name/note/wireBlocked/holidayDate).
  updateBankHoliday: (id, body) =>
    losHttp
      .put(`/closing/bank-holidays/${id}`, body, { logName: "Update Bank Holiday" })
      .then((r) => r.data),

  // DELETE /closing/bank-holidays/{id} → { deleted: 1 }.
  deleteBankHoliday: (id) =>
    losHttp
      .delete(`/closing/bank-holidays/${id}`, { logName: "Delete Bank Holiday" })
      .then((r) => r.data),

  // GET /closing/wire-blocked?date=yyyy-MM-dd → { wireBlocked, name, note }.
  getWireBlocked: (date) =>
    losHttp
      .get("/closing/wire-blocked", { params: { date }, logName: "Wire Blocked?" })
      .then((r) => r.data),

  // ── Timezone (profile) ─────────────────────────────────────────────────────
  // GET /closing/timezones → { timezones: [...] } (supported IANA set).
  getTimezones: () =>
    losHttp.get("/closing/timezones", { logName: "Timezones" }).then((r) => r.data),

  // PUT /closing/me/timezone { timezone } → { timezone }.
  setMyTimezone: (timezone) =>
    losHttp
      .put("/closing/me/timezone", { timezone }, { logName: "Set Timezone" })
      .then((r) => r.data),
};

export default closingCoverageApi;
